import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Shared 7-day date-strip used by the record-list pages (Articles / Review
 * Queue, Socials, Notifications, Announcements). Optional per-date `counts`
 * render a small badge over each day so operators can see where records are
 * without navigating day-by-day.
 */

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

function todayYMD(): string {
  return ymd(new Date());
}

function formatDayPill(date: string): { weekday: string; day: string } {
  const d = new Date(date + 'T00:00:00');
  const weekday = d.toLocaleDateString(undefined, { weekday: 'short' });
  const day = `${d.getDate()}/${d.getMonth() + 1}`;
  return { weekday, day };
}

export interface DateBarProps {
  days: string[];
  selectedDate: string;
  onSelect: (date: string) => void;
  onShiftWeek: (delta: number) => void;
  onToday: () => void;
  /** Optional per-date record counts (keyed 'YYYY-MM-DD'). Days with a
   *  positive count show a badge. */
  counts?: Record<string, number>;
}

export function DateBar({ days, selectedDate, onSelect, onShiftWeek, onToday, counts }: DateBarProps) {
  const today = todayYMD();
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-center">
      {/* Phone: week controls on their own row so all 7 day pills get the
       *  full width below. sm+: single row, unchanged. */}
      <div className="flex items-center justify-between sm:hidden">
        <button
          onClick={() => onShiftWeek(-1)}
          className="p-2.5 -ml-2 rounded-md text-grey-600 hover:bg-grey-100 active:bg-grey-200 touch-target"
          aria-label="Previous week"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          onClick={onToday}
          className="px-4 py-2 text-xs font-medium rounded-md border border-grey-300 text-grey-700 hover:bg-grey-50 active:bg-grey-100"
        >
          Today
        </button>
        <button
          onClick={() => onShiftWeek(1)}
          className="p-2.5 -mr-2 rounded-md text-grey-600 hover:bg-grey-100 active:bg-grey-200 touch-target"
          aria-label="Next week"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <button
        onClick={() => onShiftWeek(-1)}
        className="hidden sm:block p-2 rounded-md text-grey-600 hover:bg-grey-100"
        aria-label="Previous week"
        title="Previous week"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <div className="flex-1 grid grid-cols-7 gap-0.5 sm:gap-1">
        {days.map((d) => {
          const isSelected = d === selectedDate;
          const isToday = d === today;
          const isFuture = d > today;
          const { weekday, day } = formatDayPill(d);
          const count = counts?.[d] ?? 0;
          return (
            <button
              key={d}
              disabled={isFuture}
              onClick={() => onSelect(d)}
              className={`relative px-0.5 py-2 sm:px-2 rounded-md text-center transition-colors text-xs ${
                isSelected
                  ? 'bg-primary-600 text-white'
                  : isFuture
                    ? 'text-grey-300 cursor-not-allowed'
                    : isToday
                      ? 'bg-grey-100 text-grey-900 hover:bg-grey-200'
                      : 'text-grey-600 hover:bg-grey-50'
              }`}
            >
              {count > 0 && (
                <span
                  title={`${count} record${count === 1 ? '' : 's'}`}
                  className={`absolute -top-1.5 -right-1.5 min-w-[1.1rem] h-[1.1rem] px-1 flex items-center justify-center rounded-full text-[10px] font-semibold leading-none ${
                    isSelected
                      ? 'bg-surface text-primary-700'
                      : 'bg-primary-600 text-white'
                  }`}
                >
                  {count > 99 ? '99+' : count}
                </span>
              )}
              <div className="text-[10px] font-semibold uppercase tracking-wide">{weekday}</div>
              <div className="text-sm font-medium">{day}</div>
            </button>
          );
        })}
      </div>
      <button
        onClick={() => onShiftWeek(1)}
        className="hidden sm:block p-2 rounded-md text-grey-600 hover:bg-grey-100"
        aria-label="Next week"
        title="Next week"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
      <button
        onClick={onToday}
        className="hidden sm:block ml-2 px-3 py-1.5 text-xs font-medium rounded-md border border-grey-300 text-grey-700 hover:bg-grey-50"
      >
        Today
      </button>
    </div>
  );
}
